import { Realtime, debug, setAdapters } from 'leancloud-realtime/core';
import EventEmitter from 'eventemitter3';
import { LiveQueryPlugin } from 'leancloud-realtime-plugin-live-query';

const KEY_SUBSCRIPTION_ID = 'subscription-id';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var LiveQueryState;
(function (LiveQueryState) {
    LiveQueryState["READY"] = "READY";
    LiveQueryState["CONNECTING"] = "CONNECTING";
    LiveQueryState["INIT_CLIENT"] = "INIT_CLIENT";
    LiveQueryState["CONNECTED"] = "CONNECTED";
    LiveQueryState["CLOSING"] = "CLOSING";
    LiveQueryState["CLOSED"] = "CLOSED";
})(LiveQueryState || (LiveQueryState = {}));
class Subscription extends EventEmitter {
    constructor(query, _options) {
        super();
        this.query = query;
        this._options = _options;
        this._state = LiveQueryState.READY;
    }
    get state() {
        return this._state;
    }
    subscribe() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._state !== LiveQueryState.READY) {
                throw new Error(`订阅失败，当前状态：${this._state}`);
            }
            this._state = LiveQueryState.CONNECTING;
            const { app } = this.query;
            try {
                const subscriptionID = yield app.localStorage.getAsync(KEY_SUBSCRIPTION_ID);
                const { id, query_id } = yield subscribe(this.query, Object.assign(Object.assign({}, this._options), { subscriptionID }));
                this._state = LiveQueryState.INIT_CLIENT;
                if (id !== subscriptionID) {
                    yield app.localStorage.setAsync(KEY_SUBSCRIPTION_ID, id);
                }
                this._id = id;
                this._query_id = query_id;
                this._onMessage = (messages) => {
                    messages.forEach((msg) => {
                        if (msg.query_id === query_id) {
                            const obj = this.query.decodeObject(msg.object);
                            this.emit(msg.op, obj, msg.updatedKeys);
                        }
                    });
                };
                this._onReconnect = () => subscribe(this.query, Object.assign(Object.assign({}, this._options), { subscriptionID: id }))
                    .then(() => app.log.info('live-query', { message: 'reconnected', id }))
                    .catch((error) => app.log.error('live-query', { message: 'reconnect failed', error }));
                this._client = yield createLiveQueryClient(app, id);
                this._client.register(this);
                this._client.on('message', this._onMessage);
                this._client.on('reconnect', this._onReconnect);
                this._state = LiveQueryState.CONNECTED;
                return this;
            }
            catch (error) {
                app.log.error('live-query', { message: 'something wrong, unsubscribe', error });
                this.unsubscribe();
                throw error;
            }
        });
    }
    unsubscribe() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (this._state === LiveQueryState.CONNECTING) {
                this._state = LiveQueryState.CLOSED;
                return;
            }
            if (this._state !== LiveQueryState.INIT_CLIENT && this._state !== LiveQueryState.CONNECTED) {
                throw new Error(`退订失败，当前状态：${this._state}`);
            }
            this._state = LiveQueryState.CLOSING;
            try {
                (_a = this._client) === null || _a === void 0 ? void 0 : _a.off('message', this._onMessage);
                (_b = this._client) === null || _b === void 0 ? void 0 : _b.off('reconnect', this._onReconnect);
                (_c = this._client) === null || _c === void 0 ? void 0 : _c.deregister(this);
                yield this.query.app.request({
                    method: 'POST',
                    path: '/1.1/LiveQuery/unsubscribe',
                    body: {
                        id: this._id,
                        query_id: this._query_id,
                    },
                }, this._options);
            }
            finally {
                this._state = LiveQueryState.CLOSED;
            }
        });
    }
}
function subscribe(query, options) {
    const { app, className, params } = query;
    return app.request({
        method: 'POST',
        path: '/1.1/LiveQuery/subscribe',
        body: {
            id: (options === null || options === void 0 ? void 0 : options.subscriptionID) || undefined,
            query: {
                className,
                where: params.where || {},
                returnACL: params.returnACL || undefined,
            },
        },
    }, options);
}
function getRealtimeInstance(app) {
    if (!app.payload.realtime) {
        app.log.trace('live-query', { message: 'create realtime instance', app });
        app.payload.realtime = new Realtime({
            appId: app.appId,
            appKey: app.appKey,
            server: app.serverURL,
            plugins: [LiveQueryPlugin],
        });
    }
    return app.payload.realtime;
}
function createLiveQueryClient(app, subscriptionID) {
    return getRealtimeInstance(app).createLiveQueryClient(subscriptionID);
}

function setAuthHooks(authClass) {
    authClass.afterLogin(function () {
        return this.app.localStorage.removeAsync(KEY_SUBSCRIPTION_ID);
    });
    authClass.beforeLogOut(function () {
        return this.app.localStorage.removeAsync(KEY_SUBSCRIPTION_ID);
    });
}
const liveQueryModule = {
    name: 'liveQuery',
    components: { debug },
    onLoad: (runtime) => {
        const { adapters, event, modules } = runtime;
        setAdapters(adapters);
        event.on('adapters:set', setAdapters);
        const { App, Query } = modules.core.components;
        App.prototype.pause = function () {
            var _a;
            (_a = this.payload.realtime) === null || _a === void 0 ? void 0 : _a.pause();
        };
        App.prototype.resume = function () {
            var _a;
            (_a = this.payload.realtime) === null || _a === void 0 ? void 0 : _a.resume();
        };
        Query.prototype.subscribe = function (options) {
            return new Subscription(this, options).subscribe();
        };
        if (modules.auth) {
            setAuthHooks(modules.auth.components.Auth);
        }
        else {
            const listener = (module) => {
                if (module.name === 'auth') {
                    event.off('module:load', listener);
                    setAuthHooks(module.components.Auth);
                }
            };
            event.on('module:load', listener);
        }
    },
};

export { liveQueryModule };
